import { intersection } from 'lodash';

import { AdminWorkspaceRoles } from '~/data/user/role';
import { MyWorkspaceWidgetNames } from '~/scenes/MyWorkspace/components/Widgets/data';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import { WorkspacesSettings } from '~/scenes/MyWorkspace/hooks';
import { WorkspaceSettings } from '~/scenes/MyWorkspace/userSettings';
import { isRoleHRAdmin } from '~/services/Permissions';

export const filterWorkspaceWidgets = (
  workspaces: WorkspaceSettings[],
  availableWidgets: MyWorkspaceWidgetNames[],
): WorkspaceSettings[] => {
  return workspaces.map((workspace) => ({
    ...workspace,
    widgets: workspace.widgets.filter(({ widget }) => {
      return availableWidgets.includes(widget);
    }),
  }));
};

export const filterWorkspacesByWidgets = (
  workspaces: WorkspaceSettings[],
  availableWidgets: MyWorkspaceWidgetNames[],
): WorkspaceSettings[] => {
  const filterWorkspace = (workspace: WorkspaceSettings) =>
    workspace.widgets.filter(
      (w) => !(availableWidgets as string[]).includes(w.id),
    ).length === 0;

  return workspaces.filter((w) => filterWorkspace(w));
};

export const hasWorkspaceAdminAccess = (
  workspaceType: WorkspaceType,
  roles: AdminWorkspaceRoles[],
): boolean => {
  return workspaceType === WorkspaceType.Company && isRoleHRAdmin(roles);
};

export const filterWorkspacesByRolesRestrictions = (
  workspaces: WorkspaceSettings[],
  roles: AdminWorkspaceRoles[],
): WorkspaceSettings[] => {
  return workspaces.filter((workspace) => {
    const workspaceType: WorkspaceType = workspace.type as WorkspaceType;

    // Unrestricted or is not Company workspace
    if (
      workspace.restrictToRoles.length === 0 ||
      workspaceType !== WorkspaceType.Company
    ) {
      return true;
    }

    // Exception: HR_ADMIN can always edit Company Workspaces
    if (hasWorkspaceAdminAccess(workspaceType, roles)) {
      return true;
    }

    // Check for restricted access
    return intersection(workspace.restrictToRoles, roles).length > 0;
  });
};

export const filterAdminOnlyWorkspaces = (
  workspaces: WorkspaceSettings[],
  roles: AdminWorkspaceRoles[],
): WorkspaceSettings[] => {
  return workspaces.filter((workspace) => {
    return (
      workspace.restrictToRoles.length > 0 &&
      intersection(workspace.restrictToRoles, roles).length === 0
    );
  });
};

export const prepWorkspacesDefaults = (
  workspaces: WorkspaceSettings[],
  defaultWorkspaceType: WorkspaceType,
): WorkspaceSettings[] => {
  return workspaces.map((workspace) => ({
    ...workspace,
    type: workspace.type || defaultWorkspaceType,
    restrictToRoles: workspace.restrictToRoles || [],
  }));
};

export const filterUniqueWorkspaces = (workspaces: WorkspaceSettings[]) =>
  workspaces.filter(
    (workspace, index, self) =>
      self.findIndex((v) => v.id === workspace.id) === index,
  );

export const getGeneralWorkspacesSettingsResolver = (props: {
  roles: string[];
  workspacesType: WorkspaceType;
}) => {
  const { roles, workspacesType } = props;

  const noWorkspaces: WorkspacesSettings = {
    workspaces: [],
  };

  return (
    remoteWorkspacesPreferences?: Partial<WorkspacesSettings>,
  ): WorkspacesSettings => {
    try {
      const otherWorkspaceSettings =
        workspacesType === WorkspaceType.Personal &&
        !!remoteWorkspacesPreferences?.activeWorkspaceId
          ? {
              activeWorkspaceId: remoteWorkspacesPreferences.activeWorkspaceId,
              useWorkspaceAsHomepage:
                remoteWorkspacesPreferences.useWorkspaceAsHomepage,
            }
          : {};

      if (!remoteWorkspacesPreferences?.workspaces?.length) {
        return { ...noWorkspaces, ...otherWorkspaceSettings };
      }

      return {
        workspaces: filterUniqueWorkspaces(
          prepWorkspacesDefaults(
            filterWorkspacesByRolesRestrictions(
              remoteWorkspacesPreferences.workspaces,
              roles as AdminWorkspaceRoles[],
            ),
            workspacesType,
          ),
        ),
        ...otherWorkspaceSettings,
      };
    } catch {
      return noWorkspaces;
    }
  };
};
